import { useCallback, useEffect, useState } from "react";
import { Dismiss20Regular } from "@fluentui/react-icons";
import {
  ApiError,
  createArticleCategory,
  deleteArticleCategory,
  getArticleCategories,
  updateArticleCategory,
  type ArticleCategory,
  type ArticleCategoryPayload,
} from "../../../catalogo/catalogoApi";

export function ArticleCategoriesTab() {
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [nome, setNome] = useState("");
  const [codigo, setCodigo] = useState("");

  const [editingCategory, setEditingCategory] = useState<ArticleCategory | null>(null);
  const [draft, setDraft] = useState<ArticleCategoryPayload | null>(null);

  const loadCategories = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      setCategories(await getArticleCategories());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar as categorias.");
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const resetForm = () => {
    setNome("");
    setCodigo("");
  };

  const handleAdd = async () => {
    if (isSaving) return;
    if (!nome.trim()) {
      setError("A designação é obrigatória.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const created = await createArticleCategory({
        name: nome.trim(),
        code: codigo.trim() !== "" ? codigo.trim() : null,
      });
      setCategories((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível adicionar a categoria.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (categoryId: number) => {
    if (deletingId !== null) return;
    if (!window.confirm("Eliminar esta categoria?")) return;
    setDeletingId(categoryId);
    setError(null);
    try {
      await deleteArticleCategory(categoryId);
      setCategories((prev) => prev.filter((item) => item.id !== categoryId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setCategories((prev) => prev.filter((item) => item.id !== categoryId));
      } else {
        setError(err instanceof Error ? err.message : "Não foi possível eliminar a categoria.");
        loadCategories(true);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const openEdit = (category: ArticleCategory) => {
    setEditingCategory(category);
    setDraft({
      name: category.name ?? "",
      code: category.code ?? "",
    });
  };

  const saveEdit = async () => {
    if (!editingCategory || !draft) return;
    if (!draft.name.trim()) {
      setError("A designação é obrigatória.");
      return;
    }
    setError(null);
    try {
      const updated = await updateArticleCategory(editingCategory.id, {
        name: draft.name.trim(),
        code: draft.code && draft.code.trim() !== "" ? draft.code.trim() : null,
      });
      setCategories((prev) =>
        prev
          .map((item) => (item.id === updated.id ? updated : item))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
      setEditingCategory(null);
      setDraft(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível atualizar a categoria.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border p-5" style={{ borderRadius: "var(--radius-card)" }}>
        <h3 className="text-foreground mb-4" style={{ fontSize: "var(--text-section-title)", fontWeight: "var(--font-weight-semi-bold)" }}>Adicionar Categoria</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:items-end">
          <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
            Designação *
            <input value={nome} onChange={(event) => setNome(event.target.value)} maxLength={150} placeholder="Designação" className="px-3 py-2 border border-border rounded bg-background text-foreground" />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Código
            <input value={codigo} onChange={(event) => setCodigo(event.target.value)} maxLength={50} placeholder="Código" className="px-3 py-2 border border-border rounded bg-background text-foreground" />
          </label>
        </div>
        <div className="flex justify-end mt-4">
          <button
            type="button"
            disabled={isSaving}
            onClick={handleAdd}
            className="inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderRadius: "var(--radius)" }}
          >
            {isSaving ? "A adicionar…" : "Adicionar"}
          </button>
        </div>
      </div>

      {error && <div className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3" style={{ borderRadius: "var(--radius)" }}>{error}</div>}

      <div className="bg-card border border-border overflow-x-auto" style={{ borderRadius: "var(--radius-card)" }}>
        <table className="min-w-[480px] w-full">
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3">Designação</th>
              <th className="px-4 py-3 w-[160px]">Código</th>
              <th className="px-4 py-3 w-[140px]">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={3} className="px-4 py-6 text-center text-secondary-text">A carregar…</td></tr>
            ) : categories.length === 0 ? (
              <tr><td colSpan={3} className="px-4 py-6 text-center text-secondary-text">Sem categorias.</td></tr>
            ) : categories.map((category) => (
              <tr key={category.id} className="border-b border-border last:border-b-0 align-top">
                <td className="px-4 py-3 whitespace-normal break-words">{category.name || "—"}</td>
                <td className="px-4 py-3 break-words">{category.code || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 flex-wrap">
                    <button className="text-primary underline" type="button" onClick={() => openEdit(category)}>Editar</button>
                    <button className="text-destructive underline disabled:opacity-50 disabled:cursor-not-allowed" type="button" disabled={deletingId === category.id} onClick={() => handleDelete(category.id)}>{deletingId === category.id ? "A eliminar…" : "Eliminar"}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingCategory && draft && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 w-full max-w-2xl space-y-4" style={{ borderRadius: "var(--radius-card)" }}>
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-foreground" style={{ fontSize: "var(--text-section-title)", fontWeight: "var(--font-weight-semi-bold)" }}>Editar categoria</h3>
              <button type="button" onClick={() => { setEditingCategory(null); setDraft(null); }} aria-label="Fechar" className="p-1 rounded hover:bg-accent/30 text-secondary-text">
                <Dismiss20Regular className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
                Designação *
                <input placeholder="Designação" value={draft.name ?? ""} maxLength={150} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Código
                <input placeholder="Código" value={draft.code ?? ""} maxLength={50} onChange={(e) => setDraft({ ...draft, code: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setEditingCategory(null); setDraft(null); }} className="px-4 py-2 border border-border">Cancelar</button>
              <button type="button" onClick={saveEdit} className="px-4 py-2 bg-primary text-primary-foreground">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
